// src/features/analytics/AnalyticsApi.ts
//
// Static API class over the shared webApi singleton for the /v1/analytics
// gateway (paths WITHOUT the /v1/ prefix — buildUrl adds it). Only allowlisted
// params are sent (from, to, metric, interval, limit, finalizedOnly); the
// browser NEVER sends parentCid (it would be dropped server-side anyway).

import { webApi, type BlobResponse } from '@/shared/api/WebApi';
import type { UtcRange } from '@/shared/utils/utcRange';
import type {
  AnalyticsDimension,
  AnalyticsInterval,
  AnalyticsMediaAudienceDimension,
  AnalyticsMetric,
  AnalyticsScopeKind,
} from '@/lib/constants/analytics';
import type {
  AnalyticsEnvelope,
  AnalyticsMeta,
  AudienceData,
  BroadcastsData,
  ConcurrencyData,
  HeatmapData,
  LiveByMediaData,
  LiveData,
  MediaPeaksData,
  MediaRetentionData,
  OverviewData,
  PlaybackDailyData,
  PlaybackHourlyData,
  PlaybackWindowData,
  SubaccountsData,
  TopContentData,
  TransferData,
} from './types';

/** Resolved view scope: account = selected customer; network = authenticated top-level cid. */
export interface AnalyticsViewScope {
  kind: AnalyticsScopeKind;
  cid: number;
}

function base(scope: AnalyticsViewScope): string {
  return `analytics/${scope.kind === 'account' ? 'accounts' : 'networks'}/${scope.cid}`;
}

function mediaBase(cid: number, mediaId: string): string {
  // mediaId is the wire STRING, embedded as a path segment, never coerced to Number.
  return `analytics/accounts/${cid}/media/${encodeURIComponent(mediaId)}`;
}

/** An event window is an end-exclusive epoch-second instant pair (snapped to hours server-side). */
export interface EventWindowRange {
  from: number;
  to: number;
}

export class AnalyticsApi {
  static async getMeta(): Promise<AnalyticsMeta> {
    return webApi.get<AnalyticsMeta>('analytics/meta');
  }

  /**
   * `tz` (api ADR-0015 / dashboard ADR-009): an IANA zone; the gateway then
   * answers over LOCAL calendar days from the hourly tables. Omitted = UTC days.
   */
  static async getOverview(
    scope: AnalyticsViewScope,
    range: UtcRange,
    tz?: string
  ): Promise<AnalyticsEnvelope<OverviewData>> {
    return webApi.get(`${base(scope)}/overview`, { from: range.from, to: range.to, tz });
  }

  /** Per-day trend series (UTC days, or local days with `tz`); all three metrics per point. */
  static async getPlaybackDaily(
    scope: AnalyticsViewScope,
    range: UtcRange,
    tz?: string
  ): Promise<AnalyticsEnvelope<PlaybackDailyData>> {
    return webApi.get(`${base(scope)}/playback/daily`, { from: range.from, to: range.to, tz });
  }

  static async getTopContent(
    scope: AnalyticsViewScope,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number,
    tz?: string
  ): Promise<AnalyticsEnvelope<TopContentData>> {
    return webApi.get(`${base(scope)}/top-content`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
      tz,
    });
  }

  /** Per-hour series over the hourly states; from/to snap outward to whole hours downstream (≤ 92 days). */
  static async getPlaybackHourly(
    scope: AnalyticsViewScope,
    window: EventWindowRange
  ): Promise<AnalyticsEnvelope<PlaybackHourlyData>> {
    return webApi.get(`${base(scope)}/playback/hourly`, { from: window.from, to: window.to });
  }

  /** The event report for the scope: hour-aligned totals + minute-exact peak, effective window echoed. */
  static async getPlaybackWindow(
    scope: AnalyticsViewScope,
    window: EventWindowRange
  ): Promise<AnalyticsEnvelope<PlaybackWindowData>> {
    return webApi.get(`${base(scope)}/playback/window`, { from: window.from, to: window.to });
  }

  /** Per-stream trend (account scope only): UTC days, or local days with `tz`. */
  static async getMediaPlaybackDaily(
    cid: number,
    mediaId: string,
    range: UtcRange,
    tz?: string
  ): Promise<AnalyticsEnvelope<PlaybackDailyData>> {
    return webApi.get(`${mediaBase(cid, mediaId)}/playback/daily`, { from: range.from, to: range.to, tz });
  }

  static async getMediaPlaybackHourly(
    cid: number,
    mediaId: string,
    window: EventWindowRange
  ): Promise<AnalyticsEnvelope<PlaybackHourlyData>> {
    return webApi.get(`${mediaBase(cid, mediaId)}/playback/hourly`, { from: window.from, to: window.to });
  }

  /** Per-stream event report (account scope only). */
  static async getMediaPlaybackWindow(
    cid: number,
    mediaId: string,
    window: EventWindowRange
  ): Promise<AnalyticsEnvelope<PlaybackWindowData>> {
    return webApi.get(`${mediaBase(cid, mediaId)}/playback/window`, { from: window.from, to: window.to });
  }

  /** Per-stream concurrency series (presence; ≤ 90 days). */
  static async getMediaConcurrency(
    cid: number,
    mediaId: string,
    range: EventWindowRange,
    interval: AnalyticsInterval
  ): Promise<AnalyticsEnvelope<ConcurrencyData>> {
    return webApi.get(`${mediaBase(cid, mediaId)}/concurrency`, {
      from: range.from,
      to: range.to,
      interval,
    });
  }

  /** Broadcast windows derived from live presence (≤ 90 days back; empty for VOD). */
  static async getMediaBroadcasts(
    cid: number,
    mediaId: string,
    range: EventWindowRange
  ): Promise<AnalyticsEnvelope<BroadcastsData>> {
    return webApi.get(`${mediaBase(cid, mediaId)}/broadcasts`, { from: range.from, to: range.to });
  }

  /** By-media weekday/hour heatmap (route 9); range capped at 90 days by the caller. */
  static async getHeatmapByMedia(
    cid: number,
    mediaId: string,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<HeatmapData>> {
    return webApi.get(`analytics/accounts/${cid}/heatmap/concurrency/by-media`, {
      from: range.from,
      to: range.to,
      mediaId,
    });
  }

  static async getConcurrency(
    scope: AnalyticsViewScope,
    range: UtcRange,
    interval: AnalyticsInterval
  ): Promise<AnalyticsEnvelope<ConcurrencyData>> {
    return webApi.get(`${base(scope)}/concurrency`, {
      from: range.from,
      to: range.to,
      interval,
    });
  }

  static async getLive(scope: AnalyticsViewScope): Promise<AnalyticsEnvelope<LiveData>> {
    return webApi.get(`${base(scope)}/concurrency/live`);
  }

  /** Account scope only. */
  static async getLiveByMedia(
    cid: number,
    limit?: number
  ): Promise<AnalyticsEnvelope<LiveByMediaData>> {
    return webApi.get(`analytics/accounts/${cid}/concurrency/live/by-media`, { limit });
  }

  /** Account scope only. */
  static async getAudience(
    cid: number,
    dimension: AnalyticsDimension,
    range: UtcRange,
    metric: AnalyticsMetric
  ): Promise<AnalyticsEnvelope<AudienceData>> {
    return webApi.get(`analytics/accounts/${cid}/audience/${dimension}`, {
      from: range.from,
      to: range.to,
      metric,
    });
  }

  /**
   * Per-media audience drill-down — account scope only (Seq U-5). dimension is
   * geo (country|region|city) plus embedDomain (metrics expansion); tech
   * dimensions stay account-grain and are rejected here. mediaId is the wire
   * STRING and is embedded as a path segment, never coerced to Number.
   */
  static async getMediaAudience(
    cid: number,
    mediaId: string,
    dimension: AnalyticsMediaAudienceDimension,
    range: UtcRange,
    metric: AnalyticsMetric
  ): Promise<AnalyticsEnvelope<AudienceData>> {
    return webApi.get(
      `analytics/accounts/${cid}/media/${encodeURIComponent(mediaId)}/audience/${dimension}`,
      { from: range.from, to: range.to, metric }
    );
  }

  /** Per-media daily peak concurrency (metrics expansion) — account scope only. */
  static async getMediaPeaks(
    cid: number,
    mediaId: string,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<MediaPeaksData>> {
    return webApi.get(`analytics/accounts/${cid}/media/${encodeURIComponent(mediaId)}/peaks`, {
      from: range.from,
      to: range.to,
    });
  }

  /** Per-media audience retention (metrics expansion) — account scope only, VOD. */
  static async getMediaRetention(
    cid: number,
    mediaId: string,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<MediaRetentionData>> {
    return webApi.get(`analytics/accounts/${cid}/media/${encodeURIComponent(mediaId)}/retention`, {
      from: range.from,
      to: range.to,
    });
  }

  /** Account scope only; range capped at 90 days by the control. */
  static async getHeatmap(
    cid: number,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<HeatmapData>> {
    return webApi.get(`analytics/accounts/${cid}/heatmap/concurrency`, {
      from: range.from,
      to: range.to,
    });
  }

  static async getTransfer(
    scope: AnalyticsViewScope,
    range: UtcRange,
    finalizedOnly?: boolean
  ): Promise<AnalyticsEnvelope<TransferData>> {
    return webApi.get(`${base(scope)}/transfer`, {
      from: range.from,
      to: range.to,
      finalizedOnly: finalizedOnly ? true : undefined,
    });
  }

  /** Network scope only. */
  static async getSubaccounts(
    cid: number,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<AnalyticsEnvelope<SubaccountsData>> {
    return webApi.get(`analytics/networks/${cid}/subaccounts`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }

  static async downloadTopContentCsv(
    scope: AnalyticsViewScope,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<BlobResponse> {
    return webApi.getBlob(`${base(scope)}/exports/top-content.csv`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }

  /** Network scope only. */
  static async downloadSubaccountsCsv(
    cid: number,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<BlobResponse> {
    return webApi.getBlob(`analytics/networks/${cid}/exports/subaccounts.csv`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }
}
